import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@postdaily/ui/components/alert";
import { buttonVariants } from "@postdaily/ui/components/button";
import { Skeleton } from "@postdaily/ui/components/skeleton";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@postdaily/ui/components/tabs";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeftIcon } from "lucide-react";
import { useState } from "react";

import { ChannelAvatar } from "@/components/channels/channel-avatar";
import { ChannelGeneralSettings } from "@/components/channels/channel-general-settings";
import { ChannelPostingSchedule } from "@/components/channels/channel-posting-schedule";
import { PageHeader } from "@/components/page-header";
import { useCurrentWorkspace } from "@/hooks/use-current-workspace";
import { getChannelLabel } from "@/lib/channel-label";
import { getProviderLabel } from "@/lib/provider-labels";
import { useTRPC } from "@/utils/trpc";

export const Route = createFileRoute("/(app)/channels/$id/settings")({
  component: RouteComponent,
});

type Workspace = NonNullable<
  ReturnType<typeof useCurrentWorkspace>["currentWorkspace"]
>;
function RouteComponent() {
  const { id } = Route.useParams();
  const { currentWorkspace } = useCurrentWorkspace();

  if (!currentWorkspace) {
    return <Skeleton className="h-72 w-full" />;
  }

  return <ChannelSettingsPage workspace={currentWorkspace} accountId={id} />;
}

function ChannelSettingsPage({
  workspace,
  accountId,
}: {
  workspace: Workspace;
  accountId: string;
}) {
  const trpc = useTRPC();
  const [tab, setTab] = useState("general");
  const accountsQuery = useQuery(
    trpc.socialAccounts.list.queryOptions({ workspaceId: workspace.id }),
  );
  const account = accountsQuery.data?.find((item) => item.id === accountId);

  if (accountsQuery.isPending) {
    return <Skeleton className="h-96 w-full" />;
  }

  if (accountsQuery.isError) {
    return (
      <Alert variant="destructive">
        <AlertTitle>Channel unavailable</AlertTitle>
        <AlertDescription>{accountsQuery.error.message}</AlertDescription>
      </Alert>
    );
  }

  if (!account) {
    return (
      <div className="flex flex-col gap-4">
        <PageHeader title="Channel not found" />
        <Alert>
          <AlertTitle>Channel not found</AlertTitle>
          <AlertDescription>
            This channel may have been disconnected.
          </AlertDescription>
        </Alert>
        <Link
          to="/channels"
          className={buttonVariants({ variant: "outline", size: "sm" })}
        >
          <ArrowLeftIcon data-icon="inline-start" />
          All channels
        </Link>
      </div>
    );
  }

  const name = getChannelLabel(account);
  const handle = account.providerAccountHandle;

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        leading={
          <ChannelAvatar
            layout="overlay"
            name={name}
            avatarUrl={account.avatarUrl}
            provider={account.provider}
            size="lg"
          />
        }
        title={name}
        description={`${getProviderLabel(account.provider)}${handle ? ` · ${handle}` : ""}`}
      />

      <Tabs value={tab} onValueChange={(value) => setTab(value)}>
        <TabsList>
          <TabsTrigger value="general">General</TabsTrigger>
          <TabsTrigger value="schedule">Posting schedule</TabsTrigger>
        </TabsList>
        <TabsContent value="general">
          <ChannelGeneralSettings workspace={workspace} account={account} />
        </TabsContent>
        <TabsContent value="schedule">
          <ChannelPostingSchedule workspace={workspace} account={account} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
